import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash, PencilSimple, EnvelopeSimple } from 'phosphor-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { AppButton } from '../../../../../general/AppButton/AppButton';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import {
  AppUser,
  AppUserRole,
  Supplier,
  getUserArBranches,
  isSupplierAdminUser,
} from '@dill/dill-shared';
import AddSupplierUserModal from './AddSupplierUserModal/AddSupplierUserModal';
import EditSupplierUserModal from './EditSupplierUserModal/EditSupplierUserModal';
import { DillAvatar, DillButton, DillDropdown } from '../../../../../general/Dill';
import {
  removeSupplierUser,
  resendSupplierUserInvite,
  updateSupplier,
} from '../../../../../../redux/services/suppliersService';
import {
  getSupplierBranches,
  updateSupplierBranches,
} from '../../../../../../redux/services/supplierAdditionalDetailsService';
import { openMessageModal } from '../../../../../../redux/globalSlices/genericSlice';
import {
  updateUserSupplierBranches,
  updateUserSupplierDetails,
} from '../../../../../../redux/globalSlices/authSlice';
import { deriveSelectedRoles } from './SupplierUserRolesSection/SupplierUserRolesSection';
import { useSupplierBranchOptions } from '../../../../../../utils/hooks/useSupplierBranchOptions';
import { AppRadioButton } from '../../../../../general/AppRadioButton/AppRadioButton';

// Beyond this the remaining branches collapse into a "+N" chip, so one heavily-assigned user can't
// stretch the row.
const MAX_VISIBLE_BRANCHES = 2;

export const SupplierUsersSection = ({ supplier }: { supplier?: Supplier | null }) => {
  const dispatch = useAppDispatch();
  const { supplierUsers } = useAppSelector((state) => state.supplier);
  const user = useAppSelector((state) => state.auth.user);

  const canEdit = useMemo(() => isSupplierAdminUser({ user, supplier }), [user, supplier]);

  // `branches` moved off the Supplier doc into supplierAdditionalDetails - fetched separately
  // (and shared with BranchSettings.tsx via the same redux slice) rather than read off `supplier`.
  const { branches: supplierBranchesState, loadedSupplierId: loadedSupplierBranchesId } =
    useAppSelector((state) => state.supplierBranches);
  useEffect(() => {
    if (supplier?.id && loadedSupplierBranchesId !== supplier.id) {
      dispatch(getSupplierBranches({ supplierId: supplier.id }));
    }
  }, [supplier?.id, loadedSupplierBranchesId, dispatch]);

  const [isAddSupplierUserModalOpen, setIsAddSupplierUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [branchFilter, setBranchFilter] = useState<string[]>([]);

  const { branchOptions, isLoadingBranches } = useSupplierBranchOptions();

  const branchesByUserId = useMemo(
    () =>
      Object.fromEntries(
        (supplierUsers ?? []).map((supplierUser) => [
          supplierUser.id,
          getUserArBranches({ user: supplierUser, supplierId: supplier?.id }),
        ])
      ),
    [supplierUsers, supplier?.id]
  );

  // An unassigned user is visible under every branch, since they can see every branch's data.
  const filteredSupplierUsers = useMemo(() => {
    if (branchFilter.length === 0) return supplierUsers ?? [];
    return (supplierUsers ?? []).filter((supplierUser) => {
      const assigned = branchesByUserId[supplierUser.id] ?? [];
      if (assigned.length === 0) return true;
      return assigned.some((branch) => branchFilter.includes(branch));
    });
  }, [supplierUsers, branchFilter, branchesByUserId]);

  const handleConfirmRemove = async (userId: string) => {
    dispatch(
      openMessageModal({
        buttonText: '',
        modalType: 'LOADING',
        title: 'Removing user',
        subTitle: 'Removing user from this supplier...',
      })
    );
    const res = await dispatch(
      removeSupplierUser({
        supplierId: supplier?.id || '',
        userId,
      })
    );
    if (res.type === 'supplier/removeSupplierUser/fulfilled') {
      if (supplier && supplierBranchesState.length > 0) {
        const updatedBranches = supplierBranchesState.map((brnch) => ({
          ...brnch,
          salesRep: (brnch.salesRep ?? []).filter((rep) => rep?.id !== userId),
          tmIds: (brnch.tmIds ?? []).filter((id) => id !== userId),
        }));
        const updatedRegions = (supplier?.regions ?? []).map((r) => ({
          ...r,
          rsmIds: (r.rsmIds ?? []).filter((id) => id !== userId),
        }));
        const updatedSalesDirectorIds = (supplier?.salesDirectorIds ?? []).filter(
          (id) => id !== userId
        );

        await dispatch(
          updateSupplierBranches({
            supplierId: supplier?.id || '',
            branches: updatedBranches,
          })
        );
        await dispatch(
          updateSupplier({
            supplierId: supplier?.id || '',
            supplierDetails: {
              regions: updatedRegions,
              salesDirectorIds: updatedSalesDirectorIds,
            },
          })
        );

        dispatch(updateUserSupplierBranches(updatedBranches));
        dispatch(
          updateUserSupplierDetails({
            regions: updatedRegions,
            salesDirectorIds: updatedSalesDirectorIds,
          })
        );
      }

      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'SUCCESS',
          title: 'User Removed',
          subTitle: 'The user has been removed from this supplier',
        })
      );
    } else {
      const errorMsg = (res.payload as string) || 'Failed to remove user';
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error',
          subTitle: errorMsg,
        })
      );
    }
  };

  const handleRemoveUser = (userId: string, email: string) => {
    dispatch(
      openMessageModal({
        buttonText: 'Cancel',
        isSecondButton: true,
        secondButtonText: 'Yes, remove',
        modalType: 'WARN',
        title: 'Remove User',
        subTitle: `Are you sure you want to remove ${email || 'this user'} from this supplier?`,
        onSecondButtonClick: () => {
          handleConfirmRemove(userId);
        },
      })
    );
  };

  const handleResendInvite = async (userId: string, email: string) => {
    dispatch(
      openMessageModal({
        buttonText: '',
        modalType: 'LOADING',
        title: 'Resending invite',
        subTitle: `Resending invite to ${email || 'this user'}...`,
      })
    );
    const res = await dispatch(
      resendSupplierUserInvite({
        supplierId: supplier?.id || '',
        userId,
      })
    );
    if (res.type === 'supplier/resendSupplierUserInvite/fulfilled') {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'SUCCESS',
          title: 'Invite Sent',
          subTitle: `The invite has been resent to ${email || 'this user'}`,
        })
      );
    } else {
      const errorMsg = (res.payload as string) || 'Failed to resend invite';
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error',
          subTitle: errorMsg,
        })
      );
    }
  };

  return (
    <>
      <div>
        <div className="flex items-start justify-between mb-5">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Supplier Users</h4>
            <p className="text-xs text-gray-400 mt-0.5">
              Manage users with access to this supplier account
            </p>
            {/* The removed Branch Access column header explained what an assignment does. Without it
                a row of place names says nothing about payment requests, so state it here. */}
            {branchOptions.length > 0 && (
              <p className="text-xs text-gray-400 mt-0.5">
                Branches limit which payment requests a user can see — unassigned users see all.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {branchOptions.length > 0 && (
              <div className="w-[200px]">
                <DillDropdown
                  isMulti
                  isSearchable
                  closeMenu={false}
                  showMinimalDesign
                  isLoading={isLoadingBranches}
                  placeholder="All branches"
                  value={null}
                  multiValue={branchFilter}
                  items={branchOptions.map((branch) => ({ label: branch, value: branch }))}
                  onMultiSelectChange={(selected) => {
                    setBranchFilter((selected ?? []).map((item) => `${item.value}`));
                  }}
                />
              </div>
            )}
            <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
              <AppButton
                text="Add User"
                type="FLAT"
                onClick={() => setIsAddSupplierUserModalOpen(true)}
                icon={<Plus size={14} />}
                buttonStyles={{ fontSize: '12px', padding: '4px 12px' }}
                isDisabled={!canEdit}
              />
            </div>
          </div>
        </div>

        {supplierUsers && supplierUsers.length > 0 ? (
          <div>
            {/* Group header row - only role columns visible, creating stepped shape */}
            <div className="grid grid-cols-[1fr_80px_120px_130px_130px_80px_120px_120px_110px] gap-0 items-stretch border-l border-r border-transparent">
              <div />
              <div
                className="py-2 px-2 text-center bg-gray-50 border-t border-l border-gray-200 rounded-tl-xl flex items-center justify-center"
                style={{ gridColumn: 'span 4' }}>
                <span className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider">
                  Credit App Roles
                </span>
              </div>
              <div
                className="py-2 px-2 text-center bg-gray-50 border-t border-l border-gray-200 flex items-center justify-center"
                style={{ gridColumn: 'span 3' }}>
                <span className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider">
                  AR Roles
                </span>
              </div>
              <div className="bg-gray-50 border-t border-l border-r border-gray-200 rounded-tr-xl" />
            </div>

            {/* Sub-header row - borders applied directly for alignment */}
            <div className="grid grid-cols-[1fr_80px_120px_130px_130px_80px_120px_120px_110px] gap-0 border border-gray-200 rounded-tl-xl overflow-hidden bg-white">
              <span className="px-3 py-2 text-[14px] font-medium text-gray-500">Users</span>
              <span className="px-2 py-1.5 border-l border-gray-200 text-center">
                <span className="block text-[10px] font-medium text-gray-700">No Access</span>
                <span className="block text-[9px] text-gray-400 leading-tight">
                  No credit app access
                </span>
              </span>
              <span className="px-2 py-1.5 text-center">
                <span className="block text-[10px] font-medium text-gray-700">Sales</span>
                <span className="block text-[9px] text-gray-400 leading-tight">
                  View credit app dashboard
                </span>
              </span>
              <span className="px-2 py-1.5 text-center">
                <span className="block text-[10px] font-medium text-gray-700">Credit Analyst</span>
                <span className="block text-[9px] text-gray-400 leading-tight">
                  Review apps, no decisions
                </span>
              </span>
              <span className="px-2 py-1.5 text-center">
                <span className="block text-[10px] font-medium text-gray-700">Credit Manager</span>
                <span className="block text-[9px] text-gray-400 leading-tight">
                  Review & make decisions
                </span>
              </span>
              <span className="px-2 py-1.5 border-l border-gray-200 text-center">
                <span className="block text-[10px] font-medium text-gray-700">No Access</span>
                <span className="block text-[9px] text-gray-400 leading-tight">No AR access</span>
              </span>
              <span className="px-2 py-1.5 text-center">
                <span className="block text-[10px] font-medium text-gray-700">
                  Counter Sales Dispatch
                </span>
                <span className="block text-[9px] text-gray-400 leading-tight">
                  Payment requests & Virtual POS
                </span>
              </span>
              <span className="px-2 py-1.5 text-center">
                <span className="block text-[10px] font-medium text-gray-700">
                  Accounts Receivable
                </span>
                <span className="block text-[9px] text-gray-400 leading-tight">
                  Access to all AR tabs
                </span>
              </span>
              <span className="py-1.5 border-l border-gray-200" />
            </div>

            {/* User rows - per-row borders to keep column alignment */}
            {filteredSupplierUsers.map((supplierUser, index) => {
              const initials = `${supplierUser.firstName?.[0] ?? ''}${
                supplierUser.lastName?.[0] ?? ''
              }`;
              const isSelf = user?.email === supplierUser.email;
              const { creditApp, ar } = deriveSelectedRoles(
                (supplierUser.roles as AppUserRole[]) || []
              );
              const assignedBranches = branchesByUserId[supplierUser.id] ?? [];

              const isLast = index === filteredSupplierUsers.length - 1;

              return (
                <div
                  key={supplierUser.id}
                  className={`grid grid-cols-[1fr_80px_120px_130px_130px_80px_120px_120px_110px] gap-0 items-center border-l border-r border-b border-gray-200 bg-white hover:bg-gray-50/50 transition-colors ${
                    isLast ? 'rounded-b-xl overflow-hidden' : ''
                  }`}>
                  <div className="flex gap-2 min-w-0 px-3 py-2">
                    <DillAvatar initials={initials} size={28} />
                    <div className="flex flex-col min-w-0 gap-1">
                      <span className="text-xs font-semibold text-gray-800 capitalize truncate">
                        {supplierUser.firstName || ''} {supplierUser.lastName || ''}
                      </span>
                      <span className="text-[10px] text-gray-400 truncate">
                        {supplierUser.email}
                      </span>
                      {/* Branch access sits under the user rather than in its own column: a user on
                          six branches stacked six chips down a narrow column and stretched the row.
                          Labelled because branch values are place names and would otherwise read as
                          the user's location. Clicking opens the edit modal. */}
                      <button
                        onClick={() => setEditingUser(supplierUser)}
                        disabled={!canEdit || branchOptions.length === 0}
                        data-tooltip-id={
                          branchOptions.length === 0
                            ? 'no-branches-tooltip'
                            : !canEdit
                            ? 'no-permission-tooltip'
                            : undefined
                        }
                        className="flex flex-wrap items-center gap-1 mt-0.5 p-0 bg-transparent border-none text-left cursor-pointer group disabled:cursor-not-allowed">
                        <span
                          title="Branches limit which payment requests this user can see. Unassigned users see every branch."
                          className="text-[9px] font-medium uppercase tracking-wide text-gray-400">
                          Branches
                        </span>
                        {assignedBranches.length === 0 ? (
                          <span className="text-[9px] font-medium uppercase tracking-wide text-gray-400 group-hover:text-blue-500 transition-colors">
                            All
                          </span>
                        ) : (
                          <>
                            {assignedBranches.slice(0, MAX_VISIBLE_BRANCHES).map((branch) => (
                              <span
                                key={branch}
                                className="inline-flex items-center px-1 rounded bg-gray-100 text-[9px] font-medium uppercase tracking-wide text-gray-600">
                                {branch}
                              </span>
                            ))}
                            {assignedBranches.length > MAX_VISIBLE_BRANCHES && (
                              <span
                                title={assignedBranches.slice(MAX_VISIBLE_BRANCHES).join(', ')}
                                className="inline-flex items-center px-1 rounded bg-gray-100 text-[9px] font-medium text-gray-500">
                                +{assignedBranches.length - MAX_VISIBLE_BRANCHES}
                              </span>
                            )}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-center border-l border-gray-200 h-full items-center">
                    <AppRadioButton isChecked={creditApp === 'NO_ACCESS'} isDisabled={true} />
                  </div>
                  <div className="flex justify-center">
                    <AppRadioButton
                      isChecked={creditApp === 'CREDIT_APP_SALES'}
                      isDisabled={true}
                    />
                  </div>
                  <div className="flex justify-center">
                    <AppRadioButton
                      isChecked={creditApp === 'CREDIT_APP_REVIEWER'}
                      isDisabled={true}
                    />
                  </div>
                  <div className="flex justify-center">
                    <AppRadioButton
                      isChecked={creditApp === 'CREDIT_APP_DECISION_MAKER'}
                      isDisabled={true}
                    />
                  </div>
                  <div className="flex justify-center border-l border-gray-200/60  h-full items-center">
                    <AppRadioButton isChecked={ar === 'NO_ACCESS'} isDisabled={true} />
                  </div>
                  <div className="flex justify-center">
                    <AppRadioButton isChecked={ar === 'AR_SALES'} isDisabled={true} />
                  </div>
                  <div className="flex justify-center">
                    <AppRadioButton isChecked={ar === 'AR_ALL'} isDisabled={true} />
                  </div>
                  <div className="flex items-center justify-end gap-1 border-l border-gray-200/60 h-full pr-0.5">
                    {!isSelf && (
                      <div
                        className="flex items-center gap-1"
                        data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                        {!supplierUser?.isPasswordSet && (
                          <div data-tooltip-id={canEdit ? 'resend-invite-tooltip' : undefined}>
                            <DillButton
                              variant="icon-blue"
                              icon={<EnvelopeSimple size={14} weight="bold" />}
                              onClick={() => {
                                console.log({ supplierUser });
                                handleResendInvite(supplierUser.id, supplierUser.email || '');
                              }}
                              isDisabled={!canEdit}
                            />
                          </div>
                        )}
                        <DillButton
                          variant="icon-blue"
                          icon={<PencilSimple size={14} />}
                          onClick={() => setEditingUser(supplierUser)}
                          isDisabled={!canEdit}
                        />
                        <DillButton
                          variant="icon-red"
                          icon={<Trash size={14} />}
                          onClick={() =>
                            handleRemoveUser(supplierUser.id, supplierUser.email || '')
                          }
                          isDisabled={!canEdit}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {filteredSupplierUsers.length === 0 && (
              <div className="border-l border-r border-b border-gray-200 rounded-b-xl bg-white py-8 text-center">
                <p className="text-xs text-gray-400 m-0">
                  No users are assigned to the selected branches.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-700">No users yet</p>
            <p className="text-xs text-gray-400">
              Add users to give them access to this supplier account.
            </p>
          </div>
        )}
      </div>

      {!canEdit && (
        <ReactTooltip
          id="no-permission-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          You do not have permission to make changes
        </ReactTooltip>
      )}

      {branchOptions.length === 0 && (
        <ReactTooltip
          id="no-branches-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '280px' }}>
          None of this supplier's customers have a branch set, so there is nothing to scope by yet.
        </ReactTooltip>
      )}

      {canEdit && (
        <ReactTooltip
          id="resend-invite-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          Resend invite email
        </ReactTooltip>
      )}

      {isAddSupplierUserModalOpen && (
        <AddSupplierUserModal
          open={isAddSupplierUserModalOpen}
          handleClose={() => setIsAddSupplierUserModalOpen(false)}
          supplierId={supplier?.id || ''}
        />
      )}

      {editingUser && (
        <EditSupplierUserModal
          open={!!editingUser}
          handleClose={() => setEditingUser(null)}
          supplierId={supplier?.id || ''}
          user={editingUser}
        />
      )}
    </>
  );
};
